import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { CircularProgress, ToggleButtonGroup, ToggleButton, MenuItem, TextField, Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InputAdornment from '@mui/material/InputAdornment';
import { AddressEyebrow } from './QuoteElements';
import { FinanceData } from './FinanceData';
import Carousel from './Carousel';
import Lottie from 'lottie-react';
import gen1 from '../../images/lottie/gen1.json';
import gen2 from '../../images/lottie/gen2.json';
import gen3 from '../../images/lottie/gen3.json';
import gen4 from '../../images/lottie/gen4.json';
import LunexLogo from '../../images/lunex-power-logo.webp';
import DesignProgress from './DesignProgress';
import { DesignShots, PaymentCompare, PaymentToggle, CashSummary, Disclosures, ErrorNotice, PromotionBanner, SuccessPanel, SystemStats, CostBreakdown } from './QuoteSummary';

const PopupContainer = styled.div`
  display: flex;
  flex-direction: column;
  /* justify-content: space-around; */
  /* height: calc(100% - 190px); */
  height: calc(100vh - 190px);
  /* height: auto; */
  width: 100vw;
  margin: 0 auto;
  margin-top: 72px;
  background-color: #f8f4f4;

  border-radius: 20px;
  z-index: 10;
  overflow-y: auto;
  overflow-x: hidden;
  /* Same 1180px ceiling as QuoteGrid and the Design page, so the flow keeps one
     column width from the address field through to the contract. */
  max-width: 1180px;

  @media screen and (max-width: 1550px) {
    /* overflow-y: scroll; */
    height: calc(100vh - 190px);
  }

  @media screen and (max-width: 900px) {
    width: calc(100vw - 10px);
    /* height: calc(100vh - 132px); */
    height: 100%;
    margin: 0 auto;
    margin-top: 40px;
    margin-bottom: 80px;
    /* overflow-y: scroll; */
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  /* height: 100%; */
  width: 100%;
  justify-content: center;
  max-width: 100%;
  /* background-color: green; */

  @media screen and (max-width: 1300px) {
    width: 100%;
    flex-direction: column;
    align-items: center;
  }
`;

/**
 * The popup's title bar.
 *
 * Was a centred bold black heading held in place by two 30px spacer divs, with
 * a bare MUI icon button for close — the only piece of the flow still doing
 * that. It now reads like the Design page's top row: the address as a quiet
 * eyebrow, the title left-aligned in the same weight and ink as every other
 * heading, and a hairline close button.
 *
 * Sticky, because this panel scrolls a long way and the close button was the
 * first thing to disappear.
 */
const HeaderWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  padding: 15px 22px;
  box-sizing: border-box;
  background: #fff;
  border-bottom: 1px solid #e0e6ea;

  @media screen and (max-width: 900px){
    padding: 13px 16px;
  }
`;

const HeaderTitles = styled.div`
  min-width: 0;
`;

/* The shared address eyebrow, plus the gap this header needs under it — here it
   sits above the title rather than beside a Back button. */
const HeaderEyebrow = styled(AddressEyebrow)`
  margin-bottom: 5px;
`;

const CloseButton = styled.button`
  appearance: none;
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid #e0e6ea;
  border-radius: 50%;
  background: #fff;
  color: #6b8090;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 140ms ease, color 140ms ease, background 140ms ease;

  &:hover {
    border-color: #cfdde6;
    background: #f6fafd;
    color: #0f1b24;
  }

  &:focus-visible {
    outline: 2px solid #1ba8f1;
    outline-offset: 2px;
  }
`;



const DesignWrapper = styled.div`
  display: flex;
  gap: 30px;
  align-items: center;
  justify-content: center;
  /* height: 100%; */
  margin: auto 0;
  padding-top: 20px;
  /* background-color: green; */

  @media screen and (max-width: 600px) {
    flex-direction: column;
    padding: 0;
  }
`

const DesignImage = styled.img`
  height: 100%;
  width: auto;
  display: flex;
  max-width: 350px;
  margin: 0 auto;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2), 0 6px 20px rgba(0, 0, 0, 0.19);

  @media screen and (max-width: 1400px) {
    width: 90%;
    height: auto;
    max-height: 300px;
  }
`;

const ToggleWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  max-width: 100%;
`;

const StyledToggleButton = styled(ToggleButton)`
  width: 150px;
`

const FinanceWrapper = styled.div`
  /* display: grid; */
  /* grid-template-columns: 1fr auto; */
  /* gap: 20px; */
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;

  @media screen and (max-width: 900px) {
    /* grid-template-columns: 1fr; */
    margin: 0 auto;
    /* gap: 5px; */
    width: 98%;
  } 
`;


const DownPaymentField = styled(TextField)`
  width: 150px !important;

  @media screen and (max-width: 900px) {
    width: 100% !important;
    max-width: 150px;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  align-items: center;
`;


const TopContainerWrapper = styled.div`
  display: flex;
  justify-content: center;
  /* Was 115% for the $fin variant, which overflowed its own column and was a
     large part of the overlap. Nothing needs to exceed its track. */
  width: 100%;
`

/** Optional controls, folded away so the default path stays one decision. */
const OptionDisclosure = styled.details`
  border: 1px solid #e0e6ea;
  border-radius: 10px;
  width: 100%;
  margin-bottom: 14px;
  background: #fff;

  summary {
    padding: 9px 13px;
    font-size: 13px;
    cursor: pointer;
    color: #0B7FBD;
    font-weight: 600;
  }
  summary:focus-visible { outline: 2px solid #1BA8F1; outline-offset: 2px; }
`;

const OptionInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 13px 12px;
`;

/** Quiet section heading for the summary column. */
const SectionLabel = styled.div`
  font-size: 10px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  font-weight: 700;
  color: #8098a8;
  width: 100%;
  max-width: 420px;
  margin: 14px auto 8px;
`

const TopContainer = styled.div`
  width: 100%;
  max-width: 500px;

  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: ${({bottombar}) => (bottombar ? '0' : '20px')};
  margin: 10px;
  text-align: center;

  @media screen and (max-width: 900px) {
    gap: 5px;
    margin: 5px;
    }
`;

const Value = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
  white-space: nowrap;

  @media screen and (max-width: 900px) {
    font-size: 1.1rem;
    }
`;

const Title = styled.div`
  font-size: 0.8rem;
  font-weight: normal;
  color: #555;

  @media screen and (max-width: 900px) {
    font-size: 12px;
    }
`;

const TextFieldsWrapper = styled.div`
  display: flex;
  flex-direction: ${({$submit}) => ($submit ? 'column' : 'row')};
  justify-content: space-between;
  /* align-items: center; */
  gap: 20px;
  /* max-width: ${({submit}) => (submit ? '250px' : '100%')}; */
  margin: ${({$submit}) => ($submit ? '40px' : '10px')};
`;

const StyledTypography = styled(Typography)`
  font-size: ${({$header}) => ($header ? '1.2rem !important' : '0.85rem !important')};
  color: ${({$blue}) => ($blue ? '#1976d2' : 'black')};
  font-weight: ${({$header}) => ($header ? 'bold !important' : 'normal')};
  margin: 5px 0;
  text-align: ${({$design}) => ($design ? 'center' : 'left')};
  text-decoration: ${({$strike}) => ($strike ? 'line-through' : 'none')};
`;

const StyledHeader = styled.h2`
  font-size: 1.4rem;
  line-height: 1.15;
  letter-spacing: -0.01em;
  font-weight: 800;
  color: #0f1b24;
  margin: 0;

  @media screen and (max-width: 900px) {
    font-size: 1.15rem;
  }
`;

const StyledTypography2 = styled(Typography)`
  font-size: 0.7rem !important;
  color: grey !important;
  margin: 10px 0;
  text-align: center;
`;



const FinanceFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  max-width: 1000px;

  @media screen and (max-width: 900px) {
    flex-direction: column;
  }
`;

const ColumnContainer = styled.div`
  display: grid;
  /* Summary left, the decision column right. Fixed-width right track so the
     pricing never stretches to an unreadable width on large monitors. */
  grid-template-columns: minmax(0, 1fr) minmax(0, 460px);
  gap: 28px;
  width: 100%;
  max-width: 1060px;
  margin: 0 auto;
  padding: 0 20px 40px;
  align-items: start;
  box-sizing: border-box;

  @media screen and ((max-width: 1300px) or (max-height: 630px)) {
    grid-template-columns: minmax(0, 1fr);
    gap: 18px;
    padding: 0 12px 32px;
    justify-items: center;
  }
`;

const Column = styled.div`
  flex: 1;
  width: 100%;
  align-items: center;
  margin: 5px;
  position: relative;

  @media screen and (max-width: 900px) {
    width: 100%;
  }
`;

/**
 * Columns of the completed-quote view.
 *
 * These used to be `position: fixed` on wide screens, which took them out of
 * flow entirely — the summary column rendered underneath the pricing column and
 * the two overlapped as soon as either grew. ColumnContainer is now a real grid
 * (see below), so each column simply occupies its track and the container grows
 * to fit the taller of the two.
 */
const MainColumn = styled.div`
  min-width: 0;
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;

  @media screen and ((max-width: 1300px) or (max-height: 630px)) {
    width: 100%;
    max-width: 520px;
    margin: 0 auto;
  }
`;



const CarouselWrapper = styled.div`
  /* position: fixed;
  left: 40px; */
  /* top: 0;
  right: 0; */
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  /* background-color: red; */
  @media screen and (max-width: 1300px) {
    position: relative;
    left: 0;
  }
  
`

const PricingWrapper = styled.div`
  max-height: calc(100vh - 300px);
  /* height: 100%; */
  /* overflow-y: hidden; */
  padding: 10px;

  @media screen and (max-width: 900px) {
    max-height: 100%;
  }
`

const ButtonWrapper = styled.div`
  min-width: 200px;
  margin: 20px;
  display: flex;
  gap:10px;
  justify-content: center;
`

const StyledButton = styled(Button)`
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  margin-top: 20px;
  background-color: ${({ $back }) => ($back ? 'white' : '#1976d2')} !important;
  color: ${({ $back }) => ($back ? '#1976d2' : 'white')} !important;
  border: ${({ $back }) => ($back ? '2px solid #1976d2 !important' : 'none')};

  &:hover {
    background-color: ${({ $back }) => ($back ? '#e3f2fd' : '#1565c0')} !important;
  }
`;

const SubmitMessageWrapper = styled.div`
    width: 100%;
    margin: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;

    @media screen and (max-width: 480px) {
      width: 95%;
      margin: 0 auto;
    }

`

/**
 * The "what you're getting" list.
 *
 * Was a set of grey cards with a heavy double drop shadow, everything centred,
 * and the product photo forced into a 50x50 box — which squashed the panel, a
 * 682x1024 portrait shot, into a square. Now it matches the rest of the
 * summary: white rows on a hairline border, the photo contained rather than
 * stretched, and one aligned column each for what it is and how many.
 */
const HardwareWrapper = styled.div`
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  padding-bottom: 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HardwareRow = styled.div`
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #e0e6ea;
  border-radius: 10px;
  background: #fff;
`;

const HardwareThumb = styled.div`
  width: 46px;
  height: 46px;
  border: 1px solid #eaf0f4;
  border-radius: 8px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
`;

const HardwareImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
`;

const HardwareInfoColumn = styled.div`
  min-width: 0;
`;

const MicroLabel = styled.div`
  font-size: 10px;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  font-weight: 700;
  color: #8098a8;
`;

const HardwareName = styled.div`
  font-size: 13.5px;
  font-weight: 700;
  color: #0f1b24;
  line-height: 1.35;
`;

const HardwareQtyColumn = styled.div`
  text-align: right;
`;

const HardwareQty = styled.div`
  font-size: 13.5px;
  font-weight: 800;
  color: #3c4e5a;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;


export const LoadingWrapper = styled.div`
  width: 350px;
  height: 350px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* border: 1px solid #ccc; */
  /* background-color: #f9f9f9; */
  border-radius: 8px;
  overflow: hidden;
  padding: 10px; 
  box-sizing: border-box;
  /* padding-top: ${({$joke}) => ($joke ? '120px' : '5px')}; */
`;

export const LoadingMessage = styled.div`
  font-size: 24px;
  font-family: monospace;
  white-space: pre-wrap; /* Allows wrapping and preserves spaces/newlines */
  word-wrap: break-word; /* Breaks long words to prevent overflow */
  overflow-wrap: break-word; /* Ensures text wraps within the container */
  line-height: 1.5; /* Improves readability with better spacing between lines */
  max-width: 100%; /* Ensures the text stays within the wrapper's width */
  /* height: 100%; */
  
`;

export const HardwareList = ({ item, quantity, name, type }) => {
  if (!item || quantity === 0) return null;

  return (
    <HardwareRow>
      <HardwareThumb>
        <HardwareImage src={item.image} alt={item.name} />
      </HardwareThumb>
      <HardwareInfoColumn>
        <MicroLabel>{type}</MicroLabel>
        <HardwareName>{name}</HardwareName>
      </HardwareInfoColumn>
      <HardwareQtyColumn>
        <MicroLabel>Qty</MicroLabel>
        <HardwareQty>{quantity}</HardwareQty>
      </HardwareQtyColumn>
    </HardwareRow>
  );
};

const PricingPopup = ({ 
  hardware, 
  onClick, 
  updateHardware, 
  updateLender, 
  updateProduct, 
  responseState, 
  formState,
  satelliteImageUrl,
  setDesignLocked,
  setDesignStatus,
  designStatus
}) => {
  const [toggleView, setToggleView] = useState('Cash');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleToggleChange = (event, newView) => {
    if (newView !== null) {
      setToggleView(newView);
    }
  };

  const handleErrorButton = () => {
    setDesignStatus('errored');
  };

  async function handleSignUpButton() {
    const updatedPaymentType = toggleView;
    updateHardware('paymentType', updatedPaymentType);
    // console.log("hardware.paymentType", updatedPaymentType);

    const updatedHardware = { 
      ...hardware, 
      paymentType: updatedPaymentType 
    };

    await handleSignUp(updatedHardware);
  }

  async function handleSignUp(updatedHardware) {
    setSubmitting(true);
    setError(false);

    // const adders = [];
    // const adder = hardware.serviceQuantity > 0 ? "Wallbox EV Charger (Pulsar Plus Level 2 - 40A) - FL Only" : "";
    // if(adder !== ""){
    //   adders.push(adder);
    // }

    // const financeType = toggleView === 'Finance' ? "Climate First Bank" : "Cash Finance";

    const data = JSON.stringify({
      "address": formState.address,
      // "adders": adders,
      // "financeType": financeType,
      "hardware": updatedHardware,
    });
    
    const config = {
      method: 'put',
      headers: { 
        'Content-Type': 'application/json', 
      },
      body: data
    };

    // The fetch was previously unguarded: a dropped connection threw straight
    // past setSubmitting(false), leaving the button spinning with no error and
    // no way to retry.
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/estimate`, config);

      if (response.ok) {
        setSuccess("You're in! Here’s what’s next:");
      } else {
        setError({
          title: "We couldn't submit your order",
          detail:
            'Your design and pricing are saved. This was a problem on our end — please try again, and if it keeps happening we can take it from here by phone.',
        });
      }
    } catch (err) {
      console.error('Sign-up submit failed', err);
      setError({
        title: 'No connection',
        detail:
          "We couldn't reach our servers. Check your internet connection and try again — nothing has been lost.",
      });
    } finally {
      setSubmitting(false);
    }
  }

  const moduleQuantity = Math.ceil(hardware.systemSize / hardware.module.watts);

  // How many inverter units the system actually needs.
  //
  //  - Microinverter      one per panel
  //  - Tesla Inverter     one per 11.5 kW of array (a large system stacks two)
  //  - everything else    a single unit, including the FoxEss PowerQ: despite
  //                       sharing Tesla's Aurora component name it is a
  //                       single all-in-one unit and is always quoted as one.
  //
  // This was previously a chain of ternaries that fell through to
  // `moduleQuantity` for anything that wasn't a Tesla inverter, so a SolarEdge
  // system reported one inverter per panel (26).
  const inverterType = hardware.inverter?.column1?.Type;
  const inverterQuantity =
    inverterType === 'Microinverter'
      ? moduleQuantity
      : hardware.inverter?.name === 'Tesla Inverter'
        ? Math.max(1, Math.ceil(hardware.systemSize / (11.5 * 1000)))
        : 1;

  return (
    <>
      <PopupContainer>
        <HeaderWrapper>
          <HeaderTitles>
            {formState?.address && <HeaderEyebrow>{formState.address}</HeaderEyebrow>}
            <StyledHeader>
              {success
                ? "You're all set"
                : designStatus === 'errored'
                  ? 'Your System Estimate'
                  : 'Your System'}
            </StyledHeader>
          </HeaderTitles>
          {(designStatus === 'completed' || designStatus === 'errored') && (
            <CloseButton type="button" onClick={onClick} aria-label="Close">
              <CloseIcon fontSize="small" />
            </CloseButton>
          )}
        </HeaderWrapper>
        <ContentWrapper>
          {(designStatus !== 'completed' && designStatus !== 'errored') ? (
            <>
              {designStatus === 'failedFinal' ? 
                <DesignWrapper>     
                  <Column>
                    {(hardware.designImage || hardware.satelliteImage) && <DesignImage src={hardware.designImage || hardware.satelliteImage} alt="Design Preview" />}
                  </Column>
                  <Column>
                    <LoadingWrapper>
                      {/* This is the manual-design path, not a crash. The old
                          copy joked about AI taking over the world, which reads
                          as a system failure at the moment the customer most
                          needs reassurance. Their pricing is valid either way. */}
                      <ErrorNotice
                        tone="warn"
                        title="Your roof needs a closer look"
                        detail="We couldn't finish the 3D design automatically, so one of our solar specialists will complete it for you. Your system size and pricing are ready — continue to see them."
                      />
                      <ButtonWrapper>
                        <StyledButton onClick={handleErrorButton}>Continue to pricing</StyledButton>
                      </ButtonWrapper>
                    </LoadingWrapper>
                    
                  </Column>
                </DesignWrapper>
                :
                <DesignWrapper>
                  <Column>
                    {/* Real pipeline progress over the customer's own roof,
                        replacing the joke feed. Runs are ~35s now and the
                        backend reports which Aurora job is active, so showing
                        actual progress beats distraction. */}
                    <DesignProgress
                      satelliteImage={hardware.satelliteImage || hardware.designImage}
                      apiRunStatus={hardware?.designInfo?.apiRunStatus}
                      designStatus={designStatus}
                    />
                  </Column>
                </DesignWrapper>
              }
            </>
          ) : (
            <ColumnContainer>
            
              <MainColumn>
                {/* Two equal frames instead of a carousel: the captures land one
                    at a time after the design is already on screen, so a
                    carousel's slide count shifted underneath the customer and
                    hid content behind a swipe most people never make. */}
                {/* Only real Aurora renders — deliberately NOT falling back to
                    hardware.designImage, which holds the satellite photo. A
                    bare roof shown here reads as the finished system. */}
                <DesignShots
                  designImage={hardware?.designInfo?.screenshotCropped}
                  sunMapImage={hardware?.designInfo?.screenshot3dSouth}
                />

                {/* Reassurance, not the decision — so these read as chips rather
                    than competing with the payment for attention. */}
                <SystemStats
                  systemSize={hardware.systemSize}
                  annualProduction={hardware.annualProduction || responseState.solarProductionMonthly * 12}
                  offset={hardware.offset}
                />

                {/* The hardware panel, moved here from a third column that was
                    `position: fixed` with `pointer-events: none` — visible but
                    ghosted off to the side. It's what the customer is buying,
                    so it belongs in the flow with the rest of the summary.
                    Reuses HardwareList for the real product photography and its
                    existing Powerwall/inverter quantity rules. */}
                <SectionLabel>What you're getting</SectionLabel>
                <HardwareWrapper>
                  <HardwareList item={hardware.module} quantity={moduleQuantity} name={hardware.module.name} type={"Modules"}/>
                  {/* Always the inverter the customer chose. This used to show
                      the Powerwall 3 in the inverter row when it was paired with
                      the Tesla Inverter, mirroring the old design-request
                      override that merged the two — that override is gone, the
                      design now carries a real Tesla inverter, and the battery
                      has its own row below. */}
                  <HardwareList
                    item={hardware.inverter}
                    quantity={inverterQuantity}
                    name={hardware.inverter.name}
                    type={"Inverter"}/>
                  <HardwareList item={hardware.battery} quantity={hardware.batteryQuantity} name={hardware.battery.name} type={"Battery"}/>
                  <HardwareList item={hardware.services} quantity={hardware.serviceQuantity} name={hardware.services.name} type={"EV Charger"}/>
                </HardwareWrapper>
              </MainColumn>
              <MainColumn>
              
                <PricingWrapper>
                {!success && 
                  <>
                      <ToggleWrapper>
                        {/* Once a payment type is confirmed the choice is
                            locked, so show only the selected option rather than
                            a control that looks changeable but isn't. */}
                        <PaymentToggle
                          value={toggleView}
                          onChange={handleToggleChange}
                          options={['Cash', 'Finance']}
                        />
                      </ToggleWrapper>
                  </>
                }
                {toggleView === 'Lease' && !success &&(
                <>
                  <TopContainerWrapper $fin>
                    <TopContainer>
                      <Column>
                        <Value>30 Years</Value>
                        <Title>Finance Term</Title>
                      </Column>
                      <Column>
                        <Value>{hardware.selectedProduct.apr} %</Value>
                        <Title>APR</Title>
                      </Column>
                      <Column>
                        <Value>${hardware.financedMonthlyPayment.toFixed(2)}</Value>
                        <Title>Financed Payment /mo</Title>
                      </Column>
                    </TopContainer>
                  </TopContainerWrapper>
                  <TextFieldsWrapper>
                    <StyledTypography $header $blue>
                      Your Monthly Bill Goes Down To
                    </StyledTypography>
                    <StyledTypography $header $blue>
                      ${hardware.financedMonthlyPayment.toFixed(2)}
                    </StyledTypography>
                  </TextFieldsWrapper>
                  <TextWrapper>
                    <StyledTypography2>
                      Lease payments may vary based on available promotions and credit approval. Consult with your leasing representative for exact terms and rates.
                    </StyledTypography2>
                  </TextWrapper>
                </>
              )}
                {toggleView === 'Finance' && !success &&(
                <>
                    {/* Finance View Content */}
                    {
                      /* Down payment folded away: most customers take the
                         default of $0, and an open number field above the
                         headline made an optional tweak look like a required
                         step. */
                      <OptionDisclosure>
                        <summary>Add a down payment (optional)</summary>
                        <OptionInner>
                          <StyledTypography>Lowers the amount financed and your monthly cost.</StyledTypography>
                          <DownPaymentField
                            label="Down Payment"
                            type="number"
                            value={hardware.downPayment}
                            onChange={(e) => {
                              let value = e.target.value;
                          
                              if (value === "") {
                                updateHardware("downPayment", "");
                              } else {
                                value = parseInt(value, 10);
                          
                                // If the parsed value is greater than totalCost, set it to totalCost
                                if (value > (hardware.totalCost)) {
                                  updateHardware("downPayment", hardware.totalCost);
                                } else if (value < 0 || isNaN(value)) {
                                  // If the value is negative or NaN, set it to 0
                                  updateHardware("downPayment", 0);
                                } else {
                                  // Otherwise, set the value entered by the user
                                  updateHardware("downPayment", value);
                                }
                              }
                            }}
                            onBlur={(e) => {
                              let value = e.target.value;
                              if (isNaN(value) || value === "") {
                                updateHardware("downPayment", 0);  // Set to 0 when blurred with empty or NaN value
                              }
                            }}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                          />
                        </OptionInner>
                      </OptionDisclosure>
                    }
                    {/* The payment against the bill they already pay, rather
                        than a bare figure. Uses the SAME pre-tax-credit number
                        this panel already headlined, so which payment is quoted
                        has not changed — only how it is framed. The delta
                        follows the sign of the difference: a blanket "you save"
                        would be false on battery-heavy quotes. */}
                    {/* Says why the payment is what it is. Both views price
                        off the discounted figure already, but a lower number
                        with nothing explaining it just reads as the price. */}
                    <PromotionBanner hardware={hardware} />
                    <PaymentCompare
                      monthlyPayment={hardware.financedMonthlyPayment}
                      currentBill={formState?.electricBill}
                      batteryQuantity={hardware.batteryQuantity}
                      batteryName={hardware?.battery?.name}
                    />
                    <br />
                    <CostBreakdown
                      hardware={hardware}
                      monthlyPayment={hardware.financedMonthlyPayment}
                    />
                    {/* <TextFieldsWrapper>
                      <StyledTypography $header $blue>
                        Your Monthly Bill Goes Down To
                      </StyledTypography>
                      <StyledTypography $header $blue>
                        ${hardware.financedMonthlyPayment.toFixed(2)}
                      </StyledTypography>
                    </TextFieldsWrapper> */}
                    <Disclosures mode="Finance" />

                  </>
                )}
                {toggleView === 'Cash' && !success && (
                  <>
                    {/* Mirrors the Finance view: one headline figure, the
                        same cost breakdown component, then terms behind a
                        disclosure — so switching tabs doesn't feel like
                        switching products. */}
                    <PromotionBanner hardware={hardware} />
                    <CashSummary hardware={hardware} currentBill={formState?.electricBill} />
                    <br />
                    <CostBreakdown hardware={hardware} />

                    <Disclosures mode="Cash" />

                  </>
                )}
                
                {/* One step. "Confirm Payment Type" only flipped a local flag
                    before revealing a second Submit button — handleSignUpButton
                    already derives paymentType from the toggle and passes it
                    explicitly, so the intermediate step gated nothing. */}
                {!success &&
                  <ButtonWrapper>
                    {submitting ? <CircularProgress size={24}/> : <StyledButton onClick={handleSignUpButton}>Sign Up Now</StyledButton>}
                  </ButtonWrapper>
                }
                {(success || error) && (
                  <SubmitMessageWrapper>
                    {error && (
                      <ErrorNotice
                        title={error.title || 'Something went wrong'}
                        detail={error.detail}
                        actionLabel="Try again"
                        onAction={handleSignUpButton}
                      />
                    )}
                    {success && (
                      <SuccessPanel
                        email={formState.email}
                        firstName={formState.name ? String(formState.name).trim().split(' ')[0] : null}
                        systemSize={hardware.systemSize}
                        mode={toggleView}
                        monthlyPayment={hardware.financedMonthlyPayment}
                        cashTotal={hardware.totalAfterPromotion}
                      />
                    )}
                  </SubmitMessageWrapper>
                )}
                <br />
                </PricingWrapper>

              </MainColumn>
            </ColumnContainer>
          )}
        </ContentWrapper>
      </PopupContainer>
    </>
  );
};

export default PricingPopup;